import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import authService from '../../api/auth/authService';

// ----------------------------------------------------------------------

export enum UserOtherPrivilegesEnum {
  MedicalCards = 1,
}

export interface OtherPrivilegeDetail {
  uopmid: number;
  privid: number;
  modifypriv?: string;
}

export interface UserOtherPrivilegesRequest {
  uopmids: {
    uopmid: number;
    privids?: number[];
  }[];
}

export interface UserOtherPrivilegesResponse {
  data: OtherPrivilegeDetail[];
  issuccess: boolean;
  statuscode: number;
  message: string;
}

interface UserOtherPrivilegesState {
  data: OtherPrivilegeDetail[];
  loading: boolean;
  error: string | null;
  loaded: boolean;
}

const initialState: UserOtherPrivilegesState = {
  data: [],
  loading: false,
  error: null,
  loaded: false,
};

// ----------------------------------------------------------------------

export const getUserOtherPrivilegesDetails = createAsyncThunk(
  'auth/getUserOtherPrivilegesDetails',
  async (requestData: UserOtherPrivilegesRequest, { rejectWithValue }) => {
    try {
      const response = await authService.getUserOtherPrivilegesDetails(requestData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  },
  {
    condition: (_, { getState }) => {
      const { userOtherPrivileges } = getState() as { userOtherPrivileges: UserOtherPrivilegesState };
      if (userOtherPrivileges.loading || userOtherPrivileges.loaded) {
        return false;
      }
    },
  }
);

const userOtherPrivilegesSlice = createSlice({
  name: 'userOtherPrivileges',
  initialState,
  reducers: {
    clearUserOtherPrivileges: (state) => {
      state.data = [];
      state.loading = false;
      state.error = null;
      state.loaded = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getUserOtherPrivilegesDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUserOtherPrivilegesDetails.fulfilled, (state, action: PayloadAction<OtherPrivilegeDetail[]>) => {
        state.loading = false;
        state.data = action.payload;
        state.error = null;
        state.loaded = true;
      })
      .addCase(getUserOtherPrivilegesDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearUserOtherPrivileges } = userOtherPrivilegesSlice.actions;

export default userOtherPrivilegesSlice.reducer;
